import React, { useEffect, useState } from 'react';

import { useMedia } from 'react-use';
import api from '../../../lib/api';
import { showToast } from '../../shared/Toast';
import { fromRole, isXDaysOlder, LOCAL_STORAGE_OTP_RECIPIENTS, ProjectModel, SbCurrentUser, SbRecipientModel, SubmissionBoardModel } from '../component/SbModel';
import {
  url_api_v1_submission_board_otp_get,
  url_api_v1_submission_board_otp_post,
} from '../../../lib/apiUrl';
import OTPInput from '../component/OtpInput';
import SbDetailsRecipient from './SbDetailsRecipient';
import logo from '../../../assets/images/filmik-horizontal-logo-black.svg'; 
import { CircleXIcon } from 'lucide-react';
import VerifyOtp from './VerifyOtp';

export interface SbDetailsRecipientProps {
  currentUser: SbCurrentUser;
  sb_recipient: SbRecipientModel;
  project: ProjectModel;
  submissionBoardModel: SubmissionBoardModel;
  project_image_thumb: string;
}

// for recipient : director
// For SB details for recipient, we dont show all parameters like SbDetails.
// from SB controller: show method
export default function SbRecipientPage({
  currentUser,
  sb_recipient,
  project,
  submissionBoardModel,
  project_image_thumb,
}: SbDetailsRecipientProps) {
  const OTP_LENGTH = 6;
  const RESEND_TIME_SECONDS = 180; // 3 minutes
  const is400_639Screen = useMedia('(min-width: 400px) and (max-width: 639px)', false);
  const [currentUserSelected, set_currentUserSelected] = useState(null);
  
  const [recipient_email, setrecipient_email] = useState('');
  const [is_recipient_otp, set_is_recipient_otp] = useState(false);
  const [showSbDetails, setShowSbDetails] = useState(false);
  const [otpValid, setOtpValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (project_image_thumb != null) {
      project.project_image_thumb = project_image_thumb;
    }
    if (currentUser) {
      currentUser.sb_recipient = sb_recipient;
      set_currentUserSelected(currentUser);
      setShowSbDetails(true);
    } else {
      if (submissionBoardModel.is_recipient_otp) {
        let present = false;
        let storedUsers = [];
        const storedUsersString = localStorage.getItem(LOCAL_STORAGE_OTP_RECIPIENTS);
        if (storedUsersString) {
          storedUsers = JSON.parse(storedUsersString);
          // [ { key_token: key_token, otp_valid: otp_valid}]
          for (let indexStore = 0; indexStore < storedUsers.length; indexStore++) {
            const element = storedUsers[indexStore];
            if (element && element.key_token == sb_recipient.key_token) {
              present = true;
              if (isXDaysOlder(element.created_at, 7)) {
                storedUsers.splice(indexStore, 1);
                const updatedUsers = [...storedUsers];
                localStorage.setItem(LOCAL_STORAGE_OTP_RECIPIENTS, JSON.stringify(updatedUsers));
                set_is_recipient_otp(true);
                fetchOtp();
              } else {
                if (currentUser == undefined) {
                  currentUser = {
                    id: 0,
                    email: sb_recipient.recipient_email,
                    first_name: '',
                    last_name: '',
                    measure_system: '',
                    subscription_tier: '',
                    role: '',
                    // following parameters are for external selector
                    key_token: sb_recipient.key_token,
                    otp_valid: element.otp_valid,
                  };
                }
                sb_recipient.otp_valid = element.otp_valid;
                currentUser.sb_recipient = sb_recipient;
                set_currentUserSelected(currentUser);
                set_is_recipient_otp(false);
                setOtpValid(true);
                setShowSbDetails(true);
              }
              break;
            }
          }
        }
        if(!present) {
          set_is_recipient_otp(true);
          fetchOtp();
        } 
      } else {
        if (currentUser == undefined) {
          currentUser = {
            id: 0,
            email: sb_recipient.recipient_email,
            first_name: '',
            last_name: '',
            measure_system: '',
            subscription_tier: '',
            role: '',
            // following parameters are for external selector
            key_token: sb_recipient.key_token,
            otp_valid: '',
          };
        }
        currentUser.sb_recipient = sb_recipient;
        set_currentUserSelected(currentUser);
        setShowSbDetails(true);
      }
    }
  }, []);
  const fetchOtp = async () => {
    try {
      let url = url_api_v1_submission_board_otp_get
        .replace(':sb_id', submissionBoardModel.id.toString())
        .replace(':project_uuid', project.uuid);
      let filter = '';
      if (sb_recipient) {
        if (sb_recipient.key_token) {
          filter += (filter == '' ? '?' : '&') + `key=${sb_recipient.key_token.trim()}`;
        }
      }
      const response = await api.get(url + filter, {});
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Error while fetching submission_boards.');
      }
      console.log(response);
      const { otp } = response.data;
      setrecipient_email(response.data.recipient_email)
    } catch (error) {
      showToast('Error while fetching submission boards.', 'danger');
    } finally {
    }
  };
  
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [completeOtp, setCompleteOtp] = useState<string | null>(null);
  const [timer, setTimer] = useState<number>(RESEND_TIME_SECONDS);
  const [resendEnabled, setResendEnabled] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Timer logic with useEffect
  useEffect(() => {
    if (resendEnabled < 0) {
      return;
    }
      const countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(countdown);
            setResendEnabled(1);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

    // Cleanup function
    return () => clearInterval(countdown);
  }, [resendEnabled]); // Dependency to restart timer when resend is enabled/disabled

  // Format the timer into MM:SS format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  const handleOtpChange = (newOtp: string[]) => {
    setOtp(newOtp);
    const otpString = newOtp.join('');
    if (otpString.length === OTP_LENGTH) {
      setCompleteOtp(otpString);
    } else {
      setCompleteOtp(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await validateCode(completeOtp);

  };
  const validateCode = async (code: string) => {
    if (code) {
      setIsSubmitting(true);
      try {
        let url = url_api_v1_submission_board_otp_post
          .replace(':sb_id', submissionBoardModel.id.toString())
          .replace(':project_uuid', project.uuid);
        let filter = '';
        if (sb_recipient) {
          if (sb_recipient.key_token) {
            filter += (filter == '' ? '?' : '&') + `key=${sb_recipient.key_token.trim()}`;
          }
        }
        const response = await api.post(url + filter, {
          otp: code,
        });
        if (response.status !== 200 && response.status !== 201) {
          throw new Error('Error while fetching submission_boards.');
        }
        console.log(response);
        const { otp_valid } = response.data;
        if (otp_valid !== '') {
          set_is_recipient_otp(false);
          sb_recipient.otp_valid = otp_valid;
          if (currentUser == undefined) {
            currentUser = {
              id: 0,
              email: sb_recipient.recipient_email,
              first_name: '',
              last_name: '',
              measure_system: '',
              subscription_tier: '',
              role: '',
              // following parameters are for external selector
              key_token: sb_recipient.key_token,
              otp_valid: sb_recipient.otp_valid,
            };
          }
          currentUser.sb_recipient = sb_recipient;
          set_currentUserSelected(currentUser);
          // Get the stored string from localStorage
          let present = false;
          let storedUsers = [];
          const storedUsersString = localStorage.getItem(LOCAL_STORAGE_OTP_RECIPIENTS);
          if (storedUsersString) {
            storedUsers = JSON.parse(storedUsersString);
            // [ { key_token: key_token, otp_valid: otp_valid}]
            for (let indexStore = 0; indexStore < storedUsers.length; indexStore++) {
              const element = storedUsers[indexStore];
              if (element && element.key_token == sb_recipient.key_token) {
                present = true;
                break;
              }
            }
          }
          if (!present){
            const newUser = {
              key_token: sb_recipient.key_token,
              otp_valid: otp_valid,
              created_at: new Date(),
            };
            const updatedUsers = [...storedUsers, newUser];
            localStorage.setItem(LOCAL_STORAGE_OTP_RECIPIENTS, JSON.stringify(updatedUsers));
          }
          setOtpValid(true);
          setShowSbDetails(true);
        } else {
          sb_recipient.otp_valid = '';
        }
      } catch (error) {
        // setErrorMessage("Invalid or expired verification code. Please try again.");
        // showToast('Error while fetching submission boards.', 'danger');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }
  }
  const handleResendClick = async () => {
    console.log('Resending OTP...');
    setTimer(RESEND_TIME_SECONDS);
    setResendEnabled(0);
    setOtp(new Array(OTP_LENGTH).fill('')); // Reset the OTP inputs
    setCompleteOtp(null);
    await fetchOtp();
  };

  // useEffect(() => {
  //   console.log(currentUser, currentUserSelected);
  // }, [currentUserSelected]);

   useEffect(() => {
    console.log( showSbDetails, submissionBoardModel.is_recipient_otp, otpValid);
  }, [ showSbDetails, submissionBoardModel.is_recipient_otp, otpValid]);

  return (
    <div className="w-full mx-auto  bg-gray-100 min-h-screen">
      {!showSbDetails && is_recipient_otp && (
        <>
        {recipient_email && recipient_email != '' && <VerifyOtp 
          method={'email'}
          contactInfo={recipient_email}
          onSuccess={async function (completeOtp: string): Promise<void> {
            setCompleteOtp(completeOtp);
            await validateCode(completeOtp);
          } } 
          onResend={function (): void {
            handleResendClick();
          } }          
          />}
       
        </>
      )}
      {currentUserSelected != null &&
        showSbDetails &&
        (submissionBoardModel.is_recipient_otp == false || // otp not needed
          (otpValid && submissionBoardModel.is_recipient_otp)) && ( // otp needed and its valid
          <SbDetailsRecipient
            fromPage={fromRole.SB_EXTERNAL_RECIPIENT}
            currentUser={currentUserSelected}
            sb_recipient={sb_recipient}
            project={project}
            submissionBoardModel={submissionBoardModel}
          ></SbDetailsRecipient>
        )}
    </div>
  );
}
